import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import HowItWorksSection from './components/HowItWorksSection';
import PrivacySection from './components/PrivacySection';
import TestimonialsSection from './components/TestimonialsSection';
import Section from './components/Section';
import { track, Events } from 'src/utils/analytics';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_LIGHT = '4px 4px 0px rgba(255,255,255,0.25)';

// ─── Hero ────────────────────────────────────────────────────────────────────

function HeroV2() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 6 },
        borderBottom: '2px solid #1e1e1e',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 6, md: 10 },
          alignItems: 'center',
        }}
      >
        {/* Left: copy */}
        <Box>
          <Box
            sx={{
              display: 'inline-block',
              backgroundColor: '#FFF9C4',
              border: '2px solid #1e1e1e',
              borderRadius: '4px',
              px: 1.5,
              py: 0.5,
              mb: 3,
              boxShadow: '2px 2px 0px #1e1e1e',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#2C2C2C',
              }}
            >
              Evidence-based writing instruction
            </Typography>
          </Box>

          <Typography
            variant="h1"
            sx={{
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontSize: { xs: '2.4rem', md: '3.5rem' },
              fontWeight: 800,
              lineHeight: 1.1,
              mb: 3,
              color: '#2C2C2C',
              letterSpacing: '-0.02em',
            }}
          >
            Every lesson deserves a{' '}
            <Box
              component="span"
              sx={{
                textDecoration: 'underline',
                textDecorationColor: '#FFB300',
                textUnderlineOffset: '6px',
                textDecorationThickness: '4px',
              }}
            >
              real audience
            </Box>
          </Typography>

          <Typography
            sx={{
              fontFamily: 'Lato, Inter, sans-serif',
              fontSize: { xs: '1.1rem', md: '1.2rem' },
              lineHeight: 1.75,
              color: '#555',
              mb: 5,
              maxWidth: 500,
            }}
          >
            Pressto gives teachers research-backed writing scaffolds and gives
            students a real classroom newspaper to publish in. Writing gets
            better when it goes somewhere.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            <MuiButton
              variant="contained"
              onClick={() => {
                track(Events.ClickedCTA, { location: 'hero_v2', label: 'Try it free', destination: '/register' });
                navigate('/register');
              }}
              sx={{
                backgroundColor: '#2C2C2C',
                color: '#FFFFFF',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: '6px',
                border: '2px solid #1e1e1e',
                boxShadow: SHADOW,
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#1e1e1e',
                  boxShadow: '2px 2px 0px #1e1e1e',
                  transform: 'translate(2px, 2px)',
                },
                transition: 'all 100ms ease-in-out',
              }}
            >
              Try it free →
            </MuiButton>
            <MuiButton
              variant="outlined"
              onClick={() => {
                track(Events.ClickedCTA, { location: 'hero_v2', label: 'For districts', destination: '/contact' });
                navigate('/contact');
              }}
              sx={{
                borderColor: '#2C2C2C',
                border: '2px solid #2C2C2C',
                color: '#2C2C2C',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: '6px',
                boxShadow: SHADOW,
                px: 4,
                py: 1.5,
                backgroundColor: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                  borderColor: '#2C2C2C',
                  boxShadow: '2px 2px 0px #1e1e1e',
                  transform: 'translate(2px, 2px)',
                },
                transition: 'all 100ms ease-in-out',
              }}
            >
              For districts
            </MuiButton>
          </Box>

          {/* Trust line */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '0.85rem', color: '#888' }}>
              Free for teachers &bull; No credit card &bull; FERPA compliant
            </Typography>
          </Box>
        </Box>

        {/* Right: product mockup */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <ProductMockup />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Product Mockup ───────────────────────────────────────────────────────────

function ProductMockup() {
  return (
    <Box sx={{ position: 'relative' }}>
      {/* Editor panel (back) */}
      <Box
        sx={{
          position: 'absolute',
          top: -16,
          left: -16,
          width: '80%',
          backgroundColor: '#F7F7F7',
          border: '2px solid #1e1e1e',
          borderRadius: '8px',
          boxShadow: '6px 6px 0px #1e1e1e',
          p: 2.5,
          zIndex: 1,
        }}
      >
        {/* Editor toolbar */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
          {['📝 Opinion', '📰 News', '📖 Review'].map((label) => (
            <Box
              key={label}
              sx={{
                fontSize: '0.65rem',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                px: 1,
                py: 0.5,
                border: '1.5px solid #1e1e1e',
                borderRadius: '4px',
                backgroundColor: label.startsWith('📝') ? '#FFE082' : '#fff',
                boxShadow: label.startsWith('📝') ? '1px 1px 0px #1e1e1e' : 'none',
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.8rem', mb: 1.5, color: '#2C2C2C' }}>
          My Opinion on School Lunch
        </Typography>
        {/* Scaffold blocks */}
        {[
          { label: 'Hook', starter: 'Have you ever wondered why...', color: '#E3F2FD' },
          { label: 'Claim', starter: 'I believe that the cafeteria should...', color: '#F3E5F5' },
          { label: 'Reason', starter: 'One reason is that students...', color: '#E8F5E9' },
        ].map((block) => (
          <Box
            key={block.label}
            sx={{
              mb: 1,
              p: 1,
              backgroundColor: block.color,
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          >
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, fontFamily: 'Montserrat, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
              {block.label}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', fontFamily: 'Lato, sans-serif', color: '#555', fontStyle: 'italic' }}>
              {block.starter}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Newspaper panel (front) */}
      <Box
        sx={{
          position: 'relative',
          ml: '20%',
          mt: '10%',
          backgroundColor: '#faf7f0',
          border: '2px solid #1e1e1e',
          borderRadius: '8px',
          boxShadow: '6px 6px 0px #1e1e1e',
          p: 3,
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontSize: '1.4rem',
            fontWeight: 700,
            textAlign: 'center',
            borderBottom: '3px double #1a1a1a',
            pb: 1.5,
            mb: 1,
          }}
        >
          The Classroom Times
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontSize: '0.6rem',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color: '#666',
            mb: 2,
          }}
        >
          Spring Edition &bull; Room 214
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {[
            { emoji: '🌱', title: 'School Garden Opens Its Doors', tag: 'News', featured: true },
            { emoji: '💬', title: 'Why Recess Should Be Longer', tag: 'Opinion' },
            { emoji: '📚', title: 'Dog Man: A Review', tag: 'Book Review' },
            { emoji: '🍕', title: 'Cafeteria Gets a Makeover', tag: 'News' },
          ].map((story) => (
            <Box
              key={story.title}
              sx={{
                p: 1.5,
                backgroundColor: story.featured ? '#FFF9C4' : '#FFFFFF',
                borderRadius: '4px',
                border: '1.5px solid #1e1e1e',
                boxShadow: '2px 2px 0px #1e1e1e',
              }}
            >
              <Typography sx={{ fontSize: '1rem', mb: 0.5 }}>{story.emoji}</Typography>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                {story.title}
              </Typography>
              <Box
                sx={{
                  display: 'inline-block',
                  backgroundColor: '#2C2C2C',
                  color: '#fff',
                  fontSize: '0.5rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '2px',
                }}
              >
                {story.tag}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Audience Split ───────────────────────────────────────────────────────────

function AudienceSection() {
  const navigate = useNavigate();

  return (
    <Section background="#F7F7F7">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 800,
            color: '#2C2C2C',
          }}
        >
          Who is Pressto for?
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 4,
          maxWidth: 900,
          mx: 'auto',
        }}
      >
        {/* Teacher card */}
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            border: '2px solid #1e1e1e',
            borderRadius: '8px',
            boxShadow: SHADOW,
            p: 4,
          }}
        >
          <Typography sx={{ fontSize: '2rem', mb: 2 }}>🍎</Typography>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 800,
              fontSize: '1.3rem',
              mb: 1.5,
              color: '#2C2C2C',
            }}
          >
            I&apos;m a classroom teacher
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              color: '#555',
              lineHeight: 1.7,
              mb: 3,
            }}
          >
            Use research-backed writing structures, sentence starters, and
            scaffolds — then publish student work in a real classroom newspaper.
            Free to get started.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {[
              '6 evidence-based text structures',
              'Sentence starters & writing plans',
              'Classroom newspaper, ready to publish',
              'Google Classroom sync',
            ].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#FFB300', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#444' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <MuiButton
            variant="contained"
            fullWidth
            onClick={() => {
              track(Events.ClickedCTA, { location: 'audience_teacher', label: 'Start for free', destination: '/register' });
              navigate('/register');
            }}
            sx={{
              backgroundColor: '#2C2C2C',
              color: '#fff',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '6px',
              border: '2px solid #1e1e1e',
              boxShadow: SHADOW,
              py: 1.25,
              '&:hover': { backgroundColor: '#1e1e1e', transform: 'translate(2px, 2px)', boxShadow: '2px 2px 0px #1e1e1e' },
              transition: 'all 100ms ease-in-out',
            }}
          >
            Start for free →
          </MuiButton>
        </Box>

        {/* District card */}
        <Box
          sx={{
            backgroundColor: '#2C2C2C',
            border: '2px solid #1e1e1e',
            borderRadius: '8px',
            boxShadow: SHADOW,
            p: 4,
          }}
        >
          <Typography sx={{ fontSize: '2rem', mb: 2 }}>🏫</Typography>
          <Typography
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 800,
              fontSize: '1.3rem',
              mb: 1.5,
              color: '#FFFFFF',
            }}
          >
            I&apos;m evaluating for a district
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              color: 'rgba(255,255,255,0.75)',
              lineHeight: 1.7,
              mb: 3,
            }}
          >
            See how Pressto aligns to your curriculum frameworks, ESSA
            evidence tiers, and district writing goals. Pilot programs
            available with onboarding support.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {[
              'Aligns to CCSS & ELA frameworks',
              'ESSA evidence tier documentation',
              'Clever & Google Classroom SSO',
              'RGR & structured literacy support',
            ].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#FFE082', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <MuiButton
            variant="outlined"
            fullWidth
            onClick={() => {
              track(Events.ClickedCTA, { location: 'audience_district', label: 'See district options', destination: '/contact' });
              navigate('/contact');
            }}
            sx={{
              borderColor: '#FFE082',
              border: '2px solid #FFE082',
              color: '#FFE082',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: SHADOW_LIGHT,
              py: 1.25,
              '&:hover': { backgroundColor: 'rgba(255,224,130,0.1)', transform: 'translate(2px, 2px)', boxShadow: '2px 2px 0px rgba(255,255,255,0.2)' },
              transition: 'all 100ms ease-in-out',
            }}
          >
            See district options →
          </MuiButton>
        </Box>
      </Box>
    </Section>
  );
}

// ─── Academic Credibility Strip ───────────────────────────────────────────────

function CredibilityStrip() {
  return (
    <Box
      sx={{
        backgroundColor: '#FFF9C4',
        borderTop: '2px solid #1e1e1e',
        borderBottom: '2px solid #1e1e1e',
        py: { xs: 4, md: 5 },
        px: { xs: 3, md: 6 },
        textAlign: 'center',
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Typography
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            lineHeight: 1.7,
            color: '#2C2C2C',
            fontStyle: 'italic',
            mb: 2,
          }}
        >
          &ldquo;The research is clear: students write better when they have a real purpose and a real audience.
          Pressto puts both in the hands of every classroom teacher.&rdquo;
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: '#2C2C2C',
          }}
        >
          Dr. Jeanne Paratore &mdash; Professor of Education, Boston University &bull; Pressto Academic Advisor
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Text Structures Section ──────────────────────────────────────────────────

function TextStructuresSection() {
  const structures = [
    { emoji: '📰', name: 'News Story', desc: 'Who, what, when, where, why — the inverted pyramid.' },
    { emoji: '💬', name: 'Opinion', desc: 'Claim, reasons, and evidence. Persuasion done right.' },
    { emoji: '📖', name: 'Book Review', desc: 'Summary + analysis + recommendation.' },
    { emoji: '🔬', name: 'Science Report', desc: 'Hypothesis, method, results, and conclusion.' },
    { emoji: '🗺️', name: 'How-To', desc: 'Step-by-step procedural writing with clarity.' },
    { emoji: '🎭', name: 'Personal Narrative', desc: 'First-person storytelling with structure.' },
  ];

  return (
    <Section background="#FFFFFF">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 800,
            color: '#2C2C2C',
            mb: 2,
          }}
        >
          6 research-based writing structures
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.05rem',
            color: '#666',
            maxWidth: 560,
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          Each structure comes with writing plans, sentence starters, and scaffolds
          that teachers can dial up or down. The same structure works for 2nd grade
          and 8th grade — complexity comes from the prompt, not the tool.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 3,
          maxWidth: 900,
          mx: 'auto',
        }}
      >
        {structures.map((s) => (
          <Box
            key={s.name}
            sx={{
              p: 2.5,
              border: '2px solid #1e1e1e',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              boxShadow: SHADOW,
              transition: 'all 100ms ease-in-out',
              '&:hover': { transform: 'translate(2px, 2px)', boxShadow: '2px 2px 0px #1e1e1e' },
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>{s.emoji}</Typography>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.95rem', mb: 0.75, color: '#2C2C2C' }}>
              {s.name}
            </Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#666', lineHeight: 1.6 }}>
              {s.desc}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

// ─── Blog Preview ─────────────────────────────────────────────────────────────

function BlogPreviewSection() {
  const navigate = useNavigate();

  const posts = [
    {
      slug: 'why-every-elementary-school-needs-a-student-newspaper',
      title: 'Why Every Elementary School Needs a Student Newspaper',
      date: 'Apr 1, 2026',
      tag: 'Journalism',
    },
    {
      slug: 'student-journalism-and-the-science-of-reading',
      title: 'Student Journalism and the Science of Reading: A Perfect Match',
      date: 'Mar 25, 2026',
      tag: 'Research',
    },
    {
      slug: 'opinion-writing-through-journalism',
      title: 'Teaching Opinion Writing Through Student Journalism',
      date: 'Mar 11, 2026',
      tag: 'Writing Instruction',
    },
  ];

  return (
    <Section background="#F7F7F7">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: { xs: 4, md: 6 }, flexWrap: 'wrap', gap: 2 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: { xs: '1.5rem', md: '2rem' },
            fontWeight: 800,
            color: '#2C2C2C',
          }}
        >
          From the blog
        </Typography>
        <MuiButton
          onClick={() => navigate('/blog')}
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            textTransform: 'none',
            color: '#2C2C2C',
            border: '2px solid #2C2C2C',
            borderRadius: '6px',
            px: 2.5,
            py: 0.75,
            boxShadow: '2px 2px 0px #1e1e1e',
            '&:hover': { backgroundColor: '#F5F5F5', transform: 'translate(2px, 2px)', boxShadow: 'none' },
            transition: 'all 100ms ease-in-out',
          }}
        >
          All posts →
        </MuiButton>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        {posts.map((post) => (
          <Box
            key={post.slug}
            onClick={() => navigate(`/blog/${post.slug}`)}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '2px solid #1e1e1e',
              borderRadius: '6px',
              boxShadow: SHADOW,
              p: 3,
              cursor: 'pointer',
              transition: 'all 100ms ease-in-out',
              '&:hover': { transform: 'translate(2px, 2px)', boxShadow: '2px 2px 0px #1e1e1e' },
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
                backgroundColor: '#2C2C2C',
                color: '#fff',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                px: 1,
                py: 0.4,
                borderRadius: '3px',
                mb: 2,
              }}
            >
              {post.tag}
            </Box>
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: 1.4,
                color: '#2C2C2C',
                mb: 2,
              }}
            >
              {post.title}
            </Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#999' }}>
              {post.date}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

// ─── Bottom CTA ───────────────────────────────────────────────────────────────

function CTASectionV2() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        backgroundColor: '#2C2C2C',
        borderTop: '2px solid #1e1e1e',
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        textAlign: 'center',
      }}
    >
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#FFFFFF',
            mb: 2,
          }}
        >
          Give your students a real reason to write.
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: 'rgba(255,255,255,0.7)',
            lineHeight: 1.7,
            mb: 5,
          }}
        >
          Free for teachers. Set up a classroom and launch your first assignment
          in under 10 minutes.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <MuiButton
            variant="contained"
            onClick={() => {
              track(Events.ClickedCTA, { location: 'bottom_cta_v2', label: 'Get Started Free', destination: '/register' });
              navigate('/register');
            }}
            sx={{
              backgroundColor: '#FFE082',
              color: '#2C2C2C',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '6px',
              border: '2px solid #1e1e1e',
              boxShadow: SHADOW_LIGHT,
              px: 5,
              py: 1.5,
              '&:hover': {
                backgroundColor: '#FFD54F',
                boxShadow: '2px 2px 0px rgba(255,255,255,0.3)',
                transform: 'translate(2px, 2px)',
              },
              transition: 'all 100ms ease-in-out',
            }}
          >
            Get Started Free
          </MuiButton>
          <MuiButton
            variant="outlined"
            onClick={() => {
              track(Events.ClickedCTA, { location: 'bottom_cta_v2', label: 'Contact us', destination: '/contact' });
              navigate('/contact');
            }}
            sx={{
              borderColor: '#FFFFFF',
              border: '2px solid #FFFFFF',
              color: '#FFFFFF',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: SHADOW_LIGHT,
              px: 5,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                boxShadow: '2px 2px 0px rgba(255,255,255,0.3)',
                transform: 'translate(2px, 2px)',
              },
              transition: 'all 100ms ease-in-out',
            }}
          >
            Contact us
          </MuiButton>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPageV2() {
  return (
    <LandingLayout>
      <HeroV2 />
      <CredibilityStrip />
      <AudienceSection />
      <TextStructuresSection />
      <HowItWorksSection />
      <PrivacySection />
      <TestimonialsSection />
      <BlogPreviewSection />
      <CTASectionV2 />
    </LandingLayout>
  );
}
